import { Platform } from 'react-native';

/**
 * Image extensions the work-order attachment pipeline can serve back
 * (mirrors the image half of the extension map in `api/files.ts`).
 */
const IMAGE_EXTENSIONS: Record<string, string> = {
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	webp: 'image/webp',
	bmp: 'image/bmp',
	heic: 'image/heic',
	heif: 'image/heif',
};

/**
 * True when a file is an image the app can attach to a work order.
 *
 * Mime type wins when present, but Finder/Safari routinely hand back an
 * empty `type` for HEIC on drop, so the extension is a real fallback and
 * not just belt-and-braces. Unlike `isUnsupportedWebImage` below, this is
 * a pure predicate — no Platform gate — so it can run on either platform.
 */
export const isImageFile = (fileName?: string | null, mimeType?: string | null): boolean => {
	if ((mimeType || '').trim().toLowerCase().startsWith('image/')) return true;
	const ext = (fileName || '').split('.').pop()?.trim().toLowerCase() ?? '';
	// A name with no dot yields the whole name here; the lookup still fails.
	return ext !== '' && ext in IMAGE_EXTENSIONS;
};

/** Best-effort mime type for an image file whose browser-reported type is blank. */
export const inferImageMimeType = (fileName?: string | null): string => {
	const ext = (fileName || '').split('.').pop()?.trim().toLowerCase() ?? '';
	return IMAGE_EXTENSIONS[ext] ?? 'application/octet-stream';
};

/**
 * Detect HEIC/HEIF files that web browsers can't decode natively.
 * Returns true only on web when the file is HEIC/HEIF.
 */
export const isUnsupportedWebImage = (fileName?: string | null, mimeType?: string | null): boolean => {
	if (Platform.OS !== 'web') return false;
	const ext = (fileName || '').split('.').pop()?.toLowerCase();
	if (ext === 'heic' || ext === 'heif') return true;
	const mime = (mimeType || '').toLowerCase();
	return mime === 'image/heic' || mime === 'image/heif';
};

/**
 * Trigger a browser file download from a Blob or data URL.
 * Web-only — no-ops on native.
 */
export const triggerBrowserDownload = (urlOrDataUri: string, fileName: string): void => {
	if (Platform.OS !== 'web') return;
	const link = document.createElement('a');
	link.href = urlOrDataUri;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Download a Blob as a file in the browser, creating and revoking an object URL.
 * Web-only — no-ops on native.
 */
export const downloadBlobAsFile = (blob: Blob, fileName: string): void => {
	if (Platform.OS !== 'web') return;
	const url = window.URL.createObjectURL(blob);
	triggerBrowserDownload(url, fileName);
	window.URL.revokeObjectURL(url);
};
